import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SelectWithSearch from "@components/common/SelectWithSearch/SelectWithSearch";
import InputField from "@components/common/InputField/components/InputField";

interface SelectOption {
	value: string;
	label: string;
}

interface EmploymentFormDropdownsProps {
	employerLabel: string;
	employerValue: string;
	organizationName?: string;
	useOrganizationNameInput?: boolean;
	jobTypeValue: string;
	disabled: boolean;
	statusId?: string;
	employerOptions: SelectOption[];
	jobTypeOptions: SelectOption[];
	loadingEmployers: boolean;
	loadingJobTypes: boolean;
	onEmployerChange: (uuid: string, businessName: string) => void;
	onOrganizationNameChange?: (value: string) => void;
	onJobTypeChange: (uuid: string) => void;
	onEmployerSearchChange: (term: string) => void;
	onJobTypeSearchChange: (term: string) => void;
	fetchNextEmployers: () => void;
	fetchNextJobTypes: () => void;
	hasNextEmployers: boolean;
	hasNextJobTypes: boolean;
	isFetchingNextEmployers: boolean;
	isFetchingNextJobTypes: boolean;
	index: number;
}

const EmploymentFormDropdowns = ({
	employerLabel,
	employerValue,
	organizationName = "",
	useOrganizationNameInput = false,
	jobTypeValue,
	disabled,
	statusId,
	employerOptions,
	jobTypeOptions,
	loadingEmployers,
	loadingJobTypes,
	onEmployerChange,
	onOrganizationNameChange,
	onJobTypeChange,
	onEmployerSearchChange,
	onJobTypeSearchChange,
	fetchNextEmployers,
	fetchNextJobTypes,
	hasNextEmployers,
	hasNextJobTypes,
	isFetchingNextEmployers,
	isFetchingNextJobTypes,
	index,
}: EmploymentFormDropdownsProps) => {
	const { t } = useTranslation();
	const entityLabelLower = employerLabel.toLocaleLowerCase();

	const handleEmployerSelect = (uuid: string) => {
		const businessName = employerOptions.find(option => option.value === uuid)?.label ?? "";
		onEmployerChange(uuid, businessName);
	};

	const selectEntityPlaceholder = useMemo(
		() => t("feature.status.form.selectEntity", { entity: entityLabelLower }),
		[t, entityLabelLower],
	);
	const enterEntityPlaceholder = useMemo(
		() => t("feature.status.form.enterEntityName", { entity: entityLabelLower }),
		[t, entityLabelLower],
	);
	const searchEntityPlaceholder = useMemo(
		() => t("feature.status.form.searchEntity", { entity: entityLabelLower }),
		[t, entityLabelLower],
	);

	return (
		<div className="col-span-full grid grid-cols-1 gap-x-10 gap-y-5 md:grid-cols-2">
			{useOrganizationNameInput ? (
				<InputField
					id={`organization-name-${index}`}
					label={employerLabel}
					type="text"
					placeholder={enterEntityPlaceholder}
					value={organizationName}
					onChange={e => onOrganizationNameChange?.(e.target.value)}
					required
					disabled={disabled}
				/>
			) : (
				<SelectWithSearch
					id={`employer-${index}`}
					label={employerLabel}
					placeholder={selectEntityPlaceholder}
					value={employerValue}
					onChange={handleEmployerSelect}
					options={employerOptions}
					required
					disabled={disabled}
					loading={loadingEmployers}
					isSearchEnabled
					searchPlaceholder={searchEntityPlaceholder}
					onSearchChange={onEmployerSearchChange}
					onScrollToBottom={fetchNextEmployers}
					hasNextPage={hasNextEmployers}
					isFetchingNextPage={isFetchingNextEmployers}
				/>
			)}
			<SelectWithSearch
				id={`job-type-${index}`}
				label={t("feature.status.form.jobTypes")}
				placeholder={t("feature.status.form.selectJobType")}
				value={jobTypeValue}
				onChange={onJobTypeChange}
				options={jobTypeOptions}
				required
				disabled={disabled || !statusId}
				loading={loadingJobTypes}
				isSearchEnabled
				searchPlaceholder={t("feature.status.form.searchJobTypes")}
				onSearchChange={onJobTypeSearchChange}
				onScrollToBottom={fetchNextJobTypes}
				hasNextPage={hasNextJobTypes}
				isFetchingNextPage={isFetchingNextJobTypes}
			/>
		</div>
	);
};

export default EmploymentFormDropdowns;
